import { ReactElement, memo, ComponentProps } from 'react';

import { useIntl } from 'react-intl';

import Flag, { AutoDismissFlag } from '@atlaskit/flag';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';

import { FlagText, SimpleFlagProps } from 'src/redux/flags/types';

import { FlagDescription } from './flag-description';
import * as styles from './simple-flag.style';

export enum AppearanceTypes {
  error = 'error',
  info = 'info',
  normal = 'normal',
  success = 'success',
  warning = 'warning',
}

const icons: { [K in AppearanceTypes]: ReactElement } = {
  error: (
    <StatusErrorIcon
      spacing="spacious"
      label="Error"
      color={token('color.icon.danger')}
    />
  ),
  info: (
    <StatusInformationIcon
      spacing="spacious"
      label="Info"
      color={token('color.icon.discovery')}
    />
  ),
  normal: (
    <StatusInformationIcon
      spacing="spacious"
      label="Info"
      color={token('color.icon.discovery')}
    />
  ),
  success: (
    <CheckCircleIcon
      spacing="spacious"
      label="Success"
      color={token('color.icon.success')}
    />
  ),
  warning: (
    <StatusWarningIcon
      spacing="spacious"
      label="Warning"
      color={token('color.icon.warning')}
    />
  ),
};

const SimpleFlag = memo((props: SimpleFlagProps) => {
  const {
    actions,
    nonIntlActions, // ability to pass in non-Intl actions for Forge
    autoDismiss,
    coloredAppearance,
    description,
    iconType,
    id,
    title,
    props: additionalProps = {},
    sentryErrorProps,
    // FlagGroup passes other props through to its Children that need to be preserved
    ...akProps
  } = props;

  const intl = useIntl();
  const hasSentryId = Boolean(sentryErrorProps?.sentryId);

  const extractIntlMessage = (text: FlagText) =>
    typeof text === 'string' ? text : intl.formatMessage(text.msg, text.values);

  const intlActions = actions
    ? actions.map(action => ({
        content: extractIntlMessage(action.content),
        href: action.href,
        target: action.target,
        onClick: action.onClick,
      }))
    : undefined;

  const intlTitle = extractIntlMessage(title);
  const intlDescription = hasSentryId ? (
    <FlagDescription
      sentryErrorProps={sentryErrorProps}
      description={description && extractIntlMessage(description)}
    />
  ) : description ? (
    <styles.SimpleFlagDescription>
      {extractIntlMessage(description)}
    </styles.SimpleFlagDescription>
  ) : undefined;

  const flagProps: ComponentProps<typeof Flag> = {
    ...akProps,
    actions: nonIntlActions || intlActions,
    appearance: coloredAppearance && !hasSentryId ? iconType : 'normal',
    description: intlDescription,
    icon: hasSentryId ? icons.error : iconType ? icons[iconType] : icons.normal,
    id,
    title: intlTitle,
    // any Flag props explicitly passed to the SimpleFlag component
    // will take precedent over other props computed here
    ...additionalProps,
  };

  return autoDismiss && !hasSentryId ? (
    <AutoDismissFlag key={id} {...flagProps} />
  ) : (
    <Flag key={id} {...flagProps} />
  );
});

// eslint-disable-next-line import/no-default-export
export default SimpleFlag;
